import {makeAutoObservable} from 'mobx';
import {toast} from 'react-toastify';
import LoadingModule from '../../../modules/LoadingModule/LoadingModule';
import ProjectRepository from '../../../data/repositories/ProjectRepository';
import CoursesRepository from '../../../data/repositories/CoursesRepository';
import MicroreelsRepository from '../../../data/repositories/MicroreelsRepository';
import {Project} from '../../../data/models/Projects/Project';
import {CourseListItem} from '../../../data/models/Course/Course';
import {MicroreelGroup} from '../../../data/models/Microreels/MicroreelGroup';
import {getErrorFromCode} from '../../../data/errorCodes';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {projectsText} from '../projectsText';

export const PROJECT_ITEMS_PER_PAGE = 12;

class ProjectsStore {
  private loadingModule = new LoadingModule();
  private projectRepo = new ProjectRepository();
  private coursesRepo = new CoursesRepository();
  private microreelsRepo = new MicroreelsRepository();

  public projects: Project[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  private handleErrors(errors?: string[]) {
    if (errors && errors[0]) {
      toast.error(getErrorFromCode(errors[0]), toastConfig);
    }
  }

  public async fetchProjects(organizationId: string) {
    this.loadingModule.startLoading();
    const result = await this.projectRepo.getProjects(organizationId);
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      this.projects = result.data;
    } else {
      this.handleErrors(result.errors);
    }
    return this.projects;
  }

  public async getProject(organizationId: string, projectId: string) {
    const result = await this.projectRepo.getProject(organizationId, projectId);
    if (result.success && result.data) {
      return result.data;
    }
    this.handleErrors(result.errors);
    return undefined;
  }

  public async getProjectCourses(
    organizationId: string,
    projectId: string,
    page: number,
  ) {
    const result = await this.coursesRepo.getOrganizationCourses(
      organizationId,
      page,
      PROJECT_ITEMS_PER_PAGE,
      '',
      '',
      false,
      {projectId},
    );
    if (result.success && result.data) {
      return result.data;
    }
    this.handleErrors(result.errors);
    return {pagesCount: 0, data: [] as CourseListItem[]};
  }

  public async getProjectMicroreels(
    organizationId: string,
    projectId: string,
    page: number,
  ) {
    const result = await this.microreelsRepo.getMicroreelsList(
      organizationId,
      page,
      PROJECT_ITEMS_PER_PAGE,
      '',
      {projectId},
    );
    if (result.success && result.data) {
      return result.data;
    }
    this.handleErrors(result.errors);
    return {pagesCount: 0, data: [] as MicroreelGroup[]};
  }

  public async createProject(organizationId: string, name: string) {
    this.loadingModule.startLoading();
    const result = await this.projectRepo.createProject(organizationId, name);
    this.loadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return undefined;
    }
    await this.fetchProjects(organizationId);
    toast.success(projectsText.projectCreated, toastConfig);
    return result.data?.projectId;
  }

  public async editProject(
    organizationId: string,
    projectId: string,
    name: string,
  ) {
    this.loadingModule.startLoading();
    const result = await this.projectRepo.editProject(
      organizationId,
      projectId,
      name,
    );
    this.loadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    await this.fetchProjects(organizationId);
    toast.success(projectsText.projectUpdated, toastConfig);
    return true;
  }

  public async deleteProject(organizationId: string, projectId: string) {
    this.loadingModule.startLoading();
    const result = await this.projectRepo.deleteProject(
      organizationId,
      projectId,
    );
    this.loadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    await this.fetchProjects(organizationId);
    toast.success(projectsText.projectDeleted, toastConfig);
    return true;
  }

  public async assignCourses(
    organizationId: string,
    projectId: string | null,
    courseIds: string[],
  ) {
    const result = await this.projectRepo.assignCourses(
      organizationId,
      projectId,
      courseIds,
    );
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    return true;
  }
}

export default ProjectsStore;
